(() => {
  const root = document.querySelector('.shelf-scene');
  if (!root) return;
  const viewport = root.querySelector('.shelf-scene-window');
  const track = root.querySelector('.shelf-scene-track');
  const originals = [...track.querySelectorAll('.shelf-scene-card')], total = originals.length;
  const prev = root.querySelector('[data-shelf-prev]'), next = root.querySelector('[data-shelf-next]');
  const count = root.querySelector('.shelf-scene-count');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  // The tilt values are per card and never inherited, so a change restyles one card, not its contents.
  try {
    CSS.registerProperty({ name: '--shelf-angle', syntax: '<angle>', inherits: false, initialValue: '0deg' });
    CSS.registerProperty({ name: '--shelf-scale', syntax: '<number>', inherits: false, initialValue: '1' });
  } catch (e) {}
  function copy(card) {
    const clone = card.cloneNode(true);
    clone.setAttribute('aria-hidden','true'); clone.tabIndex = -1;
    clone.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
    return clone;
  }
  track.prepend(...originals.map(copy)); track.append(...originals.map(copy));
  const cards = [...track.children];
  // Card geometry is measured once per resize; frames only compute from it.
  const centers = new Float64Array(cards.length), lastAngle = new Float64Array(cards.length).fill(NaN), lastScale = new Float64Array(cards.length).fill(NaN);
  let viewW = 0, maxLeft = 0;
  let cycle = 0, stride = 0, position = 0, target = 0, frame = 0, last = 0, focused = false;
  // The drift keeps a fractional position: whole pixels go to scrollLeft (only when they change) and the
  // remainder moves the track, so every frame advances by the same fraction of a pixel.
  let written = NaN, fraction = 0;
  const DRIFT_SECONDS = 4.5;   // seconds for the drift to carry the shelf one card along
  let drag = null, moved = false, hovering = false, touching = false, visible = false, manuallyPaused = false, resumeAt = 0;
  const delay = () => { resumeAt = performance.now() + 2000; wake(); };
  const pause = document.createElement('button');
  const pauseIcon = '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" style="display:block;margin:auto" fill="currentColor"><rect x="4" y="3" width="2" height="10"/><rect x="10" y="3" width="2" height="10"/></svg>';
  const playIcon = '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" style="display:block;margin:auto" fill="currentColor"><path d="M5 3.5 12 8l-7 4.5Z"/></svg>';
  pause.type = 'button'; pause.innerHTML = pauseIcon; pause.setAttribute('aria-label','Pause shelf scrolling');
  root.querySelector('.shelf-scene-foot > div').prepend(pause);
  pause.onclick = () => { manuallyPaused = !manuallyPaused; pause.innerHTML = manuallyPaused ? playIcon : pauseIcon; pause.setAttribute('aria-label', manuallyPaused ? 'Play shelf scrolling' : 'Pause shelf scrolling'); wake(); };
  function shift(value) {
    if (Math.abs(value - fraction) < .001) return;
    fraction = value;
    track.style.transform = value ? `translate3d(${-value}px,0,0)` : '';
  }
  // The browser rounds scrollLeft to device pixels, so at 125% / 150% display scaling a write of 4795 reads back
  // 4794.857. The value read back is what's on screen: the track's offset makes up the rest, and the scroll listener
  // recognises it as the shelf's own write - comparing against the requested whole pixel instead mistook every step
  // for a visitor's scroll and pulled the drift back (2026-11-03: a third of its speed, and uneven).
  let requested = NaN;
  function apply(left) {
    const whole = Math.floor(left + 1e-6);
    // written is NaN once a resize or the visitor's own scroll has taken over: write again even at the same pixel
    if (whole !== requested || written !== written) { viewport.scrollLeft = whole; requested = whole; written = viewport.scrollLeft; }
    shift(reduced.matches ? 0 : left - written);
  }
  function measure() {
    const old = cycle, relative = old ? position / old : 1;
    stride = cards[1].offsetLeft - cards[0].offsetLeft;
    cycle = cards[total].offsetLeft - cards[0].offsetLeft;
    cards.forEach((card, i) => { centers[i] = card.offsetLeft + card.offsetWidth / 2; });
    viewW = viewport.clientWidth; maxLeft = viewport.scrollWidth - viewW;
    lastAngle.fill(NaN); lastScale.fill(NaN);
    position = target = relative * cycle; written = NaN; apply(position); paint(position);
  }
  // Pure computation from cached geometry; writes only the cards whose value changed.
  function paint(left) {
    let nearest = 0, distance = Infinity;
    const flat = reduced.matches;
    for (let i = 0; i < cards.length; i++) {
      const x = centers[i] - left - viewW / 2;
      const relative = Math.max(-1, Math.min(1, x / viewW));
      const angle = flat ? 0 : -relative * 22, scale = flat ? 1 : 1 - Math.abs(relative) * .055;
      if (Math.abs(angle - lastAngle[i]) > .005 || !(lastAngle[i] === lastAngle[i])) { cards[i].style.setProperty('--shelf-angle', `${angle}deg`); lastAngle[i] = angle; }
      if (Math.abs(scale - lastScale[i]) > .00002 || !(lastScale[i] === lastScale[i])) { cards[i].style.setProperty('--shelf-scale', scale); lastScale[i] = scale; }
      if (Math.abs(x) < distance) { distance = Math.abs(x); nearest = i; }
    }
    prev.disabled = next.disabled = false;
    const label = `${String(nearest % total + 1).padStart(2,'0')} / ${String(total).padStart(2,'0')}`;
    if(count.textContent !== label) count.textContent = label;
  }
  function wrap() {
    if (!cycle || focused) return;
    const shift = position < cycle * .5 ? cycle : position >= cycle * 1.5 ? -cycle : 0;
    position += shift; target += shift;
    if(drag) drag.left += shift;
  }
  function tick(now) {
    frame = 0;
    if(!visible || document.hidden) return;
    const dt = Math.min(now - last || 16.7,40); last = now;
    const auto = !reduced.matches && !manuallyPaused && !hovering && !touching && !drag && !focused && now >= resumeAt;
    // one card every 4.5 s, the pace of Shopify's account carousel the owner picked (2026-11-03; was 0.15 card/s)
    if(auto) target += stride / DRIFT_SECONDS * dt / 1000;
    // Only the shelf's own movement is written; while native touch scrolling or its fling owns the
    // position, the scroll listener paints and nothing is written back.
    if (auto || drag || Math.abs(target - position) > .01) {
      position += (target-position) * (reduced.matches ? 1 : 1-Math.exp(-dt/(drag ? 42 : 115)));
      if(Math.abs(target-position) < .01) position = target;
      wrap(); apply(position); paint(position);
    }
    if(!reduced.matches || Math.abs(target-position) > .01) frame = requestAnimationFrame(tick);
  }
  function wake() { if(!frame && visible && !document.hidden) { last = 0; frame = requestAnimationFrame(tick); } }
  function stop() { cancelAnimationFrame(frame); frame = 0; position = target = viewport.scrollLeft + fraction; }
  function glide(value) { target = Math.max(0, Math.min(maxLeft,value)); wake(); }
  function move(direction) { delay(); glide(target + direction*stride); }
  prev.onclick = () => move(-1); next.onclick = () => move(1);
  viewport.addEventListener('keydown', event => {
    if(event.target !== viewport) return;
    if(event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1); }
    if(event.key === 'Home' || event.key === 'End') { event.preventDefault(); delay(); glide(cycle + (event.key === 'End' ? (total-1)*stride : 0)); }
  });
  viewport.addEventListener('pointerenter', e => { if(e.pointerType === 'mouse') hovering = true; });
  viewport.addEventListener('pointerleave', e => { if(e.pointerType === 'mouse') { hovering = false; delay(); } });
  viewport.addEventListener('pointerdown', event => {
    stop(); touching = true; moved = false;
    if(event.pointerType !== 'mouse' || event.button !== 0) return;
    drag = {x:event.clientX,left:position,id:event.pointerId,lastX:event.clientX,time:performance.now(),velocity:0};
  });
  // A vertical page swipe that starts on the shelf leaves the drift running; only a sideways pan pauses it.
  let axis = null, startX = 0, startY = 0;
  viewport.addEventListener('touchstart', event => { const t = event.touches[0]; axis = null; startX = t.clientX; startY = t.clientY; }, {passive:true});
  viewport.addEventListener('touchmove', event => {
    if (axis || event.touches.length !== 1) return;
    const t = event.touches[0], dx = t.clientX - startX, dy = t.clientY - startY;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
    axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    if (axis === 'y' && !drag) { touching = false; resumeAt = 0; wake(); }
  }, {passive:true});
  viewport.addEventListener('pointermove', event => {
    if(!drag) return;
    const delta = event.clientX-drag.x, now = performance.now(), dt = Math.max(1,now-drag.time);
    drag.velocity = drag.velocity*.3+(drag.lastX-event.clientX)/dt*.7;
    drag.lastX = event.clientX; drag.time = now;
    if(Math.abs(delta)>6 || moved) {
      event.preventDefault();
      if(!moved) { moved=true; viewport.setPointerCapture(drag.id); viewport.classList.add('is-dragging'); }
      glide(drag.left-delta);
    }
  });
  function end(event) {
    if(!touching && !drag) return;
    touching=false; delay();
    if(!drag) { position=target=viewport.scrollLeft + fraction; return; }
    const finished=drag; drag=null; viewport.classList.remove('is-dragging');
    if(viewport.hasPointerCapture(finished.id)) viewport.releasePointerCapture(finished.id);
    if(moved) {
      const velocity=event.type==='pointerup' && performance.now()-finished.time<100 ? finished.velocity : 0;
      glide(target+(reduced.matches ? 0 : Math.max(-viewport.clientWidth*.6,Math.min(viewport.clientWidth*.6,velocity*180))));
    }
  }
  window.addEventListener('pointerup',end); window.addEventListener('pointercancel',end);
  viewport.addEventListener('lostpointercapture',end);
  viewport.addEventListener('click',event=>{ if(moved) { event.preventDefault(); event.stopPropagation(); moved=false; } },true);
  viewport.addEventListener('dragstart',event=>event.preventDefault());
  viewport.addEventListener('wheel',()=>{stop();delay();},{passive:true});
  viewport.addEventListener('scroll',()=> {
    const left = viewport.scrollLeft;
    if (Math.abs(left - written) < .01) return; // the shelf's own write, already painted in tick
    // Native touch, keyboard focus and wheel scrolling become the next origin (the sub-pixel remainder stays put).
    if(Math.abs(left + fraction - position)>2) delay();
    written = NaN; position = target = left + fraction; paint(position);
  },{passive:true});
  // A long fling can carry the shelf past its clones; once it settles, step back by one cycle (identical cards).
  viewport.addEventListener('scrollend', () => {
    if (touching || drag || written === written) return;
    const before = position; wrap();
    if (position !== before) { apply(position); paint(position); }
  });
  root.addEventListener('focusin', () => { focused = !!root.querySelector(':focus-visible'); });
  root.addEventListener('focusout',() => { focused = false; delay(); });
  new IntersectionObserver(entries=>{visible=entries[0].isIntersecting; if(visible) wake(); else stop();}).observe(viewport);
  new ResizeObserver(measure).observe(viewport);
  reduced.addEventListener('change',()=>{stop();shift(0);position=target=viewport.scrollLeft;pause.hidden=reduced.matches;lastAngle.fill(NaN);paint(position);wake();});
  document.addEventListener('visibilitychange',()=>{if(document.hidden)stop();else wake();});
  window.addEventListener('pagehide',stop); window.addEventListener('pageshow',wake);
  pause.hidden=reduced.matches; measure();
})();

// Approved red-disc cursor. The comparison page supplies its own cursor.
(() => {
  const area=document.querySelector('#shop-by-shelf .shelf-scene-window');
  if(!area || document.querySelector('[data-option]'))return;
  const cursor=document.createElement('div');
  cursor.className='shelf-drag-cursor';cursor.setAttribute('aria-hidden','true');
  cursor.innerHTML='<span>Drag</span>';document.body.append(cursor);
  const reduced=matchMedia('(prefers-reduced-motion:reduce)');
  let x=0,y=0,px=0,py=0,frame=0,active=false,held=false,known=false;
  function paint(){
    frame=0;
    const smoothing=reduced.matches?1:.38;
    px+=(x-px)*smoothing;py+=(y-py)*smoothing;
    cursor.style.transform=`translate3d(${px}px,${py}px,0)`;
    if(active&&(Math.abs(x-px)>.1||Math.abs(y-py)>.1))frame=requestAnimationFrame(paint);
  }
  function hide(){
    active=false;
    cursor.classList.remove('visible','held');cancelAnimationFrame(frame);frame=0;
  }
  function reconcile(){
    if(!known){if(active)hide();return;} // no class changes per scroll frame before a mouse has been seen
    const hit=document.elementFromPoint(x,y);
    const onShelf=hit&&area.contains(hit);
    const onLink=hit?.closest('.shelf-scene-link');
    if(!onShelf||(!held&&onLink)){hide();return;}
    if(!active){px=x;py=y;active=true;}
    area.classList.add('shelf-cursor-ready');
    cursor.classList.add('visible');cursor.classList.toggle('held',held);
    if(!frame)paint();
  }
  area.addEventListener('pointerenter',event=>{
    if(event.pointerType!=='mouse')return;
    known=true;x=event.clientX;y=event.clientY;area.classList.add('shelf-cursor-ready');reconcile();
  });
  area.addEventListener('pointerleave',()=>{if(!held)hide();});
  document.addEventListener('pointermove',event=>{
    if(event.pointerType!=='mouse')return;
    known=true;x=event.clientX;y=event.clientY;reconcile();
  },{passive:true,capture:true});
  area.addEventListener('pointerdown',event=>{
    if(event.pointerType!=='mouse'||event.button!==0)return;
    known=true;x=event.clientX;y=event.clientY;
    held=!event.target.closest('.shelf-scene-link');reconcile();
  });
  window.addEventListener('pointerup',event=>{
    if(event.pointerType!=='mouse')return;
    held=false;x=event.clientX;y=event.clientY;reconcile();
  });
  function reset(){known=false;held=false;hide();}
  window.addEventListener('pointercancel',reset);
  document.addEventListener('pointerout',event=>{if(!event.relatedTarget)reset();});
  document.addEventListener('scroll',reconcile,{passive:true,capture:true});
  window.addEventListener('resize',reconcile);
  window.addEventListener('blur',reset);
  document.addEventListener('visibilitychange',()=>{if(document.hidden)reset();});
  const menu=document.querySelector('.nav-study');
  if(menu)new MutationObserver(reconcile).observe(menu,{attributes:true,attributeFilter:['class']});
})();

